import * as React from "react";
import s from "./fluxo.scss";
import { AnimatedNumber } from "../../utils/use-count-up";
import { AnimatedBar } from "./animated-bar";
import Icon from "#components/icon";
import { fluxoContent } from "./content";
import { formatCompactNumber, formatNumber } from "../../utils/format-number";
import { MultiPlaceRetrospectiveData } from "../../api/types";
import { PlaceCarousel } from "../place-carousel";

interface FluxoProps {
	monthlyEvolution: Record<string, unknown>;
	total: number;
	averageTicket: number;
	checkInsCompleted: number;
	percentageVsLastYear: {
		people: number;
		ticket: number;
		checkIns: number;
	};
	type: "cashless" | "ficha" | "mixed";
	multiPlaceData?: MultiPlaceRetrospectiveData;
	selectedPlaceIndex?: number;
	onPlaceIndexChange?: (index: number) => void;
}

export function Fluxo({
	monthlyEvolution,
	total,
	averageTicket,
	checkInsCompleted,
	percentageVsLastYear,
	type,
	multiPlaceData,
	selectedPlaceIndex = 0,
	onPlaceIndexChange,
}: FluxoProps) {
	const currentData = React.useMemo(() => {
		if (!multiPlaceData)
			return {
				monthlyEvolution,
				total,
				averageTicket,
				checkInsCompleted,
				percentageVsLastYear,
				type,
			};

		const places = multiPlaceData.places;

		// Verificação de segurança: índice válido
		if (selectedPlaceIndex < 0 || selectedPlaceIndex >= places.length) {
			return {
				monthlyEvolution,
				total,
				averageTicket,
				checkInsCompleted,
				percentageVsLastYear,
				type,
			};
		}

		const currentPlace = places[selectedPlaceIndex];

		// Verificação de segurança: place existe
		if (!currentPlace) {
			return {
				monthlyEvolution,
				total,
				averageTicket,
				checkInsCompleted,
				percentageVsLastYear,
				type,
			};
		}

		if (currentPlace.id === "aggregated") {
			const invoicing = multiPlaceData.aggregated.invoicing;
			const highlights = multiPlaceData.aggregated.highlights;
			return {
				monthlyEvolution: invoicing.monthlyEvolution,
				total: invoicing.total,
				averageTicket: invoicing.averageTicket,
				checkInsCompleted: highlights.checkInsCompleted,
				percentageVsLastYear: {
					people: invoicing.totalPercentageVsLastYear,
					ticket: invoicing.averageTicketPercentageVsLastYear,
					checkIns: highlights.checkInsCompletedPercentageVsLastYear,
				},
				type: multiPlaceData.aggregated.type,
				mixedData: multiPlaceData.aggregated.mixedData,
			};
		}

		const placeData = multiPlaceData.byPlace[currentPlace.id];
		if (!placeData) {
			return {
				monthlyEvolution,
				total,
				averageTicket,
				checkInsCompleted,
				percentageVsLastYear,
				type,
			};
		}

		const invoicing = placeData.invoicing;
		const highlights = placeData.highlights;
		return {
			monthlyEvolution: invoicing.monthlyEvolution,
			total: invoicing.total,
			averageTicket: invoicing.averageTicket,
			checkInsCompleted: highlights.checkInsCompleted,
			percentageVsLastYear: {
				people: invoicing.totalPercentageVsLastYear,
				ticket: invoicing.averageTicketPercentageVsLastYear,
				checkIns: highlights.checkInsCompletedPercentageVsLastYear,
			},
			type: placeData.type,
		};
	}, [
		multiPlaceData,
		selectedPlaceIndex,
		monthlyEvolution,
		total,
		averageTicket,
		checkInsCompleted,
		percentageVsLastYear,
		type,
	]);

	const isCashless = currentData.type === "cashless";
	const isMixed = currentData.type === "mixed";
	const mixedData = (currentData as any).mixedData;
	const isAggregated = multiPlaceData?.places[selectedPlaceIndex]?.id === "aggregated";
	const shouldHideAverage = isAggregated && isMixed;
	const monthlyData =
		Object.keys(currentData.monthlyEvolution).length > 0
			? Object.entries(currentData.monthlyEvolution).map(
					([month, data]: [string, any]) => ({
						month,
						invoicing: data.invoicing || 0,
						averageTicket: data.averageTicket || 0,
					}),
			  )
			: [];

	const maxInvoicing =
		monthlyData.length > 0 ? Math.max(...monthlyData.map(d => d.invoicing)) : 0;

	return (
		<section id="fluxo" className={s.fluxo}>
			<div className={s.sectionContainer}>
				<h2 className={s.sectionTitle}>{fluxoContent.title}</h2>

				{multiPlaceData && onPlaceIndexChange && (
					<PlaceCarousel
						places={multiPlaceData.places}
						currentIndex={selectedPlaceIndex}
						onChangeIndex={onPlaceIndexChange}
					/>
				)}

				<div className={`${s.fluxoGrid} ${isMixed ? s.fluxoGridMixed : ""}`}>
					{isMixed && mixedData ? (
						<>
							{/* Cards primeiro quando for mixed */}
							<div className={`${s.fluxoSummary} ${s.fluxoSummaryMixed}`}>
								{/* Cards Cashless - mesmas cores do cashless quando não é mixed */}
								<div className={`${s.summaryCard} ${s.cardBlue}`}>
									<h3>
										<AnimatedNumber
											value={mixedData.cashless.highlights.checkInsCompleted}
											duration={1200}
											prefix="+"
											formatNumber={formatCompactNumber}
										/>
									</h3>
									<p>{fluxoContent.cashless.checkInsLabel}</p>
								</div>
								<div className={`${s.summaryCard} ${s.cardOrange}`}>
									<h3>
										<AnimatedNumber
											value={mixedData.cashless.invoicing.total}
											duration={1200}
											prefix="+"
											formatNumber={formatCompactNumber}
										/>
									</h3>
									<p>{fluxoContent.cashless.totalLabel}</p>
								</div>
								<div className={`${s.summaryCard} ${s.cardRed}`}>
									<h3>
										<AnimatedNumber
											value={mixedData.cashless.invoicing.averageTicket}
											duration={1200}
											prefix={fluxoContent.currencyPrefix}
											formatNumber={value => formatNumber(value, 0)}
										/>
									</h3>
									<p>{fluxoContent.cashless.ticketLabel}</p>
								</div>
								<div className={`${s.summaryCard} ${s.cardPurple}`}>
									<h3>
										<AnimatedNumber
											value={mixedData.ficha.invoicing.total}
											duration={1200}
											prefix="+"
											formatNumber={formatCompactNumber}
										/>
									</h3>
									<p>{fluxoContent.ficha.totalLabel}</p>
								</div>
								{/* Cards Ficha - mesmas cores do ficha quando não é mixed */}
								<div className={`${s.summaryCard} ${s.cardCyan}`}>
									<h3>
										<AnimatedNumber
											value={mixedData.ficha.highlights.checkInsCompleted}
											duration={1200}
											prefix="+"
											formatNumber={formatCompactNumber}
										/>
									</h3>
									<p>{fluxoContent.ficha.transactionsLabel}</p>
								</div>
								<div className={`${s.summaryCard} ${s.cardTeal}`}>
									<h3>
										<AnimatedNumber
											value={mixedData.ficha.invoicing.averageTicket}
											duration={1200}
											prefix={fluxoContent.currencyPrefix}
											formatNumber={value => formatNumber(value, 0)}
										/>
									</h3>
									<p>{fluxoContent.ficha.ticketLabel}</p>
								</div>
							</div>
							<p className={s.footerNote}>
								*Exclusivo para operações com comanda individual.
							</p>
							{/* Gráfico depois quando for mixed */}
							<div className={s.fluxoChart}>
								<h3>{fluxoContent.monthlyEvolution}</h3>
								{!shouldHideAverage && (
									<p className={s.chartSubtitle}>
										Faturamento e Ticket médio por pessoa pagante
									</p>
								)}
								<div className={s.chartBars}>
									{monthlyData.map((d, index) => (
										<div key={d.month} className={s.chartRow}>
											<span className={s.chartMonth}>{d.month}</span>
											<AnimatedBar
												value={d.invoicing}
												maxValue={maxInvoicing}
												label={`${fluxoContent.currencyPrefix}${formatCompactNumber(
													d.invoicing,
												)}`}
												delay={index * 50}
											/>
											{!shouldHideAverage && (
												<span className={s.chartTicket}>
													{fluxoContent.currencyPrefix}
													{formatNumber(d.averageTicket, 0)}
												</span>
											)}
										</div>
									))}
								</div>
							</div>
						</>
					) : (
						<>
							{/* Gráfico primeiro quando não é mixed */}
							<div className={s.fluxoChart}>
								<h3>{fluxoContent.monthlyEvolution}</h3>
								<p className={s.chartSubtitle}>
									{isCashless
										? fluxoContent.cashless.subtitle
										: fluxoContent.ficha.subtitle}
								</p>
								<div className={s.chartBars}>
									{monthlyData.map((d, index) => (
										<div key={d.month} className={s.chartRow}>
											<span className={s.chartMonth}>{d.month}</span>
											<AnimatedBar
												value={d.invoicing}
												maxValue={maxInvoicing}
												label={`${fluxoContent.currencyPrefix}${formatCompactNumber(
													d.invoicing,
												)}`}
												delay={index * 50}
											/>
											<span className={s.chartTicket}>
												{fluxoContent.currencyPrefix}
												{formatNumber(d.averageTicket, 0)}
											</span>
										</div>
									))}
								</div>
							</div>
							{/* Cards depois quando não é mixed */}
							<div className={s.fluxoSummary}>
								{/* Cards quando não é mixed - cores diferentes para cashless e ficha */}
								<div
									className={`${s.summaryCard} ${isCashless ? s.cardBlue : s.cardCyan}`}
								>
									<div className={s.summaryIcon}>
										<Icon
											icon={isCashless ? "ri-user-add-line" : "ri-bank-card-2-line"}
										/>
									</div>
									<h3>
										<AnimatedNumber
											value={currentData.checkInsCompleted}
											duration={1200}
											prefix="+"
											formatNumber={formatCompactNumber}
										/>
									</h3>
									<p>
										{isCashless
											? fluxoContent.cashless.checkInsLabel
											: fluxoContent.ficha.transactionsLabel}
									</p>
								</div>
								<div
									className={`${s.summaryCard} ${
										isCashless ? s.cardOrange : s.cardPurple
									}`}
								>
									<div className={s.summaryIcon}>
										<Icon icon={isCashless ? "ri-group-line" : "ri-drinks-2-line"} />
									</div>
									<h3>
										<AnimatedNumber
											value={currentData.total}
											duration={1200}
											prefix="+"
											formatNumber={formatCompactNumber}
										/>
									</h3>
									<p>
										{isCashless
											? fluxoContent.cashless.totalLabel
											: fluxoContent.ficha.totalLabel}
									</p>
								</div>
								<div
									className={`${s.summaryCard} ${isCashless ? s.cardRed : s.cardTeal}`}
								>
									<div className={s.summaryIcon}>
										<Icon icon="ri-wallet-3-line" />
									</div>
									<h3>
										<AnimatedNumber
											value={currentData.averageTicket}
											duration={1200}
											prefix={fluxoContent.currencyPrefix}
											formatNumber={value => formatNumber(value, 0)}
										/>
									</h3>
									<p>
										{isCashless
											? fluxoContent.cashless.ticketLabel
											: fluxoContent.ficha.ticketLabel}
									</p>
								</div>
							</div>
						</>
					)}
				</div>
				{!isMixed && (
					<p className={s.footerNote}>
						*Exclusivo para operações com comanda individual.
					</p>
				)}
			</div>
		</section>
	);
}
